import React, { useCallback, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import {
  ArrayInput,
  AutocompleteInput,
  FormDataConsumer,
  maxLength,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
} from "react-admin";
import StudyGroupField from "server/components/Fields/StudyGroupField";
import { settings } from "app";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import RoomInput from "server/components/Fields/RoomInput";
import { useWatch } from "react-hook-form";
import { validateEnd, validateStart } from "utils/validations";
import dataProvider from "server/dataProvider";
import { InternshipPlanScheduleTypes } from "utils/constants";
import { graduationRequests } from "api/graduationApi";
import { InternshipSessionInput } from "server/components/Fields/InternshipSessionInput";
import AcademicYearInput from "components/LearningActivity/CreateWeeklySchedule/components/Inputs/AcademicYearInput";
import { employeeRequests } from "api/employeeApi";
import { administrationRequests } from "api/administrationApi";

const SaveForm = ({ filters }) => {
  const [internshipSessionFromDate, setInternshipSessionFromDate] = useState(null);
  const [internshipSessionToDate, setInternshipSessionToDate] = useState(null);

  const studyGroups = useWatch({ name: "studyGroups" });
  const formAcademicYearId = useWatch({ name: "academicYearId" });
  const formStructuralUnitId = useWatch({ name: "structuralUnitId" });
  const formInternshipSessionId = useWatch({ name: "internshipSessionId" });
  const formPlanId = useWatch({ name: "planId" });

  const academicYearId = formAcademicYearId ?? filters?.academicYearId;
  const structuralUnitId = formStructuralUnitId ?? filters?.structuralUnitId;
  const internshipSessionId = formInternshipSessionId ?? filters?.internshipSessionId;
  const planId = formPlanId ?? filters?.formPlanId;

  const fetchInternshipSessionsDate = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        graduationRequests.getInternshipSession(internshipSessionId),
        null,
        "GET",
      );
      setInternshipSessionFromDate(data?.startDate);
      setInternshipSessionToDate(data?.endDate);
    } catch (err) {
      console.error(err);
    }
  }, [internshipSessionId]);

  useEffect(() => {
    if (!internshipSessionId) return;
    fetchInternshipSessionsDate();
  }, [internshipSessionId, fetchInternshipSessionsDate]);

  const parseStudyGroups = value => {
    if (!value) return [];

    const groups = [];
    const parts = value.split(",").map(p => p.trim());

    for (const part of parts) {
      if (part.includes("-")) {
        const [from, to] = part.split("-").map(Number);

        if (isNaN(from) || isNaN(to)) return null;

        for (let i = from; i <= to; i++) {
          groups.push(i);
        }
      } else {
        const num = Number(part);

        if (isNaN(num)) return null;

        groups.push(num);
      }
    }
    return groups;
  };

  const validateStudyGroupsInRange = (childValue, parentValue) => {
    if (!childValue) return undefined;
    if (!parentValue) return undefined;

    const parentGroups = parseStudyGroups(parentValue);
    const childGroups = parseStudyGroups(childValue);
    const invalidGroups = childGroups.filter(g => !parentGroups.includes(g));
    if (invalidGroups.length > 0) {
      return `Групи ${invalidGroups.join(", ")} са извън диапазона (${parentValue})`;
    }
  };

  return (
    <>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={6}
        >
          <AcademicYearInput withoutLanguage />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <ReferenceInput
            source="structuralUnitId"
            reference={administrationRequests.getStructuralUnitsSelect()}
            filter={{ filterByUser: true }}
          >
            <SelectInput
              label="Структурна единица"
              optionText="name"
              validate={required()}
            />
          </ReferenceInput>
        </Grid>
        {structuralUnitId && (
          <>
            <Grid
              item
              xs={12}
              md={6}
            >
              <InternshipSessionInput
                key={structuralUnitId}
                filter={{ structuralUnitId, academicYearId }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <ReferenceInput
                source="planId"
                reference={graduationRequests.getInternshipPlansSelect()}
              >
                <SelectInput
                  label="Стажантски план"
                  optionText="name"
                  emptyText={false}
                />
              </ReferenceInput>
            </Grid>
          </>
        )}
        <Grid
          item
          xs={12}
          md={8}
        >
          <TextInput
            source="name"
            label="Име"
            validate={maxLength(100)}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
        >
          <StudyGroupField
            isRequired={true}
            label="Пример за въвеждане на Учебни групи: 1-5, 8, 11-13, 15. Въвеждането е задължително!"
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={1}
      >
        <ArrayInput
          source="cycles"
          label="Цикли"
          defaultValue={[{}]}
        >
          <SimpleFormIterator
            disableReordering
            disableRemove
            disableClear
            sx={settings.style.iteratorWithoutGap}
          >
            <FormDataConsumer>
              {({ scopedFormData }) => {
                return (
                  <Grid
                    container
                    spacing={2}
                  >
                    {planId && (
                      <Grid
                        item
                        xs={12}
                        md={6}
                      >
                        <ReferenceInput
                          source="disciplineId"
                          reference={graduationRequests.getPlanDisciplinesSelect(planId)}
                          filter={{ planScheduleType: InternshipPlanScheduleTypes.Cyclical }}
                        >
                          <SelectInput
                            label="Дисциплина"
                            validate={required()}
                            optionText="nameBg"
                          />
                        </ReferenceInput>
                      </Grid>
                    )}
                    <Grid
                      item
                      xs={12}
                      md={3}
                    >
                      <CustomDateInput
                        source="fromDate"
                        label="Начална дата"
                        minDate={internshipSessionFromDate}
                        maxDate={internshipSessionToDate}
                        validate={() => validateStart(scopedFormData?.fromDate, scopedFormData?.toDate, required)}
                        noTime
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={3}
                    >
                      <CustomDateInput
                        source="toDate"
                        label="Крайна дата"
                        minDate={internshipSessionFromDate}
                        maxDate={internshipSessionToDate}
                        validate={() => validateEnd(scopedFormData?.toDate, scopedFormData?.fromDate, required)}
                        noTime
                      />
                    </Grid>
                  </Grid>
                );
              }}
            </FormDataConsumer>
            <ArrayInput
              source="employees"
              label="Преподаватели"
              defaultValue={[{}]}
            >
              <SimpleFormIterator
                disableReordering
                disableRemove
                disableClear
                sx={settings.style.iteratorWithoutGap}
              >
                <FormDataConsumer>
                  {({ scopedFormData }) => {
                    return (
                      <Grid
                        container
                        spacing={2}
                      >
                        <Grid
                          item
                          xs={12}
                          md={4}
                        >
                          <ReferenceInput
                            source="employeeId"
                            reference={employeeRequests.getProfessorsSelect()}
                          >
                            <AutocompleteInput
                              label="Преподавател"
                              optionText={rec => {
                                const scientificDegree = rec.scientificDegreeNameBg ?? "";
                                return rec.secondName
                                  ? `${scientificDegree} ${rec.firstName} ${rec.secondName} ${rec.lastName}`
                                  : `${scientificDegree} ${rec.firstName} ${rec.lastName}`;
                              }}
                              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                              validate={required()}
                            />
                          </ReferenceInput>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                        >
                          <RoomInput />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                        >
                          <ReferenceInput
                            source="buildingId"
                            reference={administrationRequests.getBuildingsSelect()}
                            label="Сграда"
                          >
                            <AutocompleteInput
                              label="Сграда"
                              optionText="name"
                              optionValue="id"
                              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                            />
                          </ReferenceInput>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          md={4}
                        >
                          <StudyGroupField
                            isRequired={true}
                            label="Пример за въвеждане на Учебни групи: 1-5, 8, 11-13, 15. Въвеждането е задължително!"
                            validate={() => validateStudyGroupsInRange(scopedFormData?.studyGroups, studyGroups)}
                          />
                        </Grid>
                      </Grid>
                    );
                  }}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>
    </>
  );
};

export default SaveForm;
